/**
 * Typed tool cards for the sentencing toolkit (TOOL_CARDS registry).
 *
 * find_sentences → one sortable table of sentences from both sources, each row saying where
 *   it is known from, with the words of a facts search marked under the row
 *   result envelope: { records: [...], returned, total_matching, by_source, left_out, filters }
 * summarize_sentencing_records → one statistic over the pool, the mix by source, two lenses
 *   result envelope: { all: {...}, doc_only: {...}, appealed_only: {...}, mix: {...}, filters }
 * search_sentencing_authority → passages and holdings, each with its pinpoint
 *   result envelope: { passages: [...], holdings: [...] }
 *
 * Every card that shows figures also shows the filter that produced them and
 * a link that opens Browse on it.
 *
 * The LLM's prose is the narrative caption; these cards own the
 * structured artifact (mirrors TimelineCard).
 */
import { useMemo, useState } from 'react';
import {
  KNOWN_FROM, KNOWN_FROM_NOTE,
  afterAppealLabel, browseLink, describeFilters, knownFromLabel, knownFromOf, leftOutLines, monthsRange, monthsShort,
  shortCite, yearsNote,
} from '../domain/sentencing';
import { OpinionLinks } from './SentencingOpinion';
import './SentencingBrowse.css';
import './SentencingCards.css';

/** «highlighted» → <mark>highlighted</mark>, as React nodes (the text is never parsed as HTML). */
export function Highlighted({ text }) {
  if (!text) return null;
  const parts = String(text).split(/(«[^»]*»)/);
  return (
    <>
      {parts.map((p, i) => (p.startsWith('«') && p.endsWith('»')
        ? <mark key={i} className="sf-hit">{p.slice(1, -1)}</mark>
        : <span key={i}>{p}</span>))}
    </>
  );
}

/** The narratives (or paragraphs) of a row's opinion that hold the search words. */
export function FactSnippets({ snippets }) {
  if (!Array.isArray(snippets) || snippets.length === 0) return null;
  return (
    <div className="sb-facts">
      {snippets.map((f, i) => (
        <div key={`${f.field}-${i}`} className="sf-fact">
          <span className="sf-fact-label">{f.label}{(f.paragraphs || []).length && f.field !== 'paragraph' ? ` (¶ ${f.paragraphs.join(', ')})` : ''}:</span>{' '}
          <Highlighted text={f.snippet} />
        </div>
      ))}
    </div>
  );
}

/** A badge saying where a sentence is known from. */
export function KnownFromBadge({ rec }) {
  const kf = knownFromOf(rec);
  return <span className={`sb-known sb-known-${kf}`} title={kf === 'opinion' ? 'Known from an opinion only: DOC holds no record of this sentence' : kf === 'both' ? 'Known from DOC and from an opinion on the case' : 'Known from DOC only: no opinion on this case'}>{knownFromLabel(kf)}</span>;
}

const RECORD_COLUMNS = [
  { key: 'defendant', label: 'Defendant' },
  { key: 'docket', label: 'Docket' },
  { key: 'county', label: 'County' },
  { key: 'judge', label: 'Judge' },
  { key: 'mca_section', label: 'MCA §' },
  { key: 'description', label: 'Offense' },
  { key: 'term', label: 'Term' },
  // DOC: where the sentence is served and how much is suspended, as DOC words it
  // ("MSP/MWP · some suspended"); DOC never gives suspended months. An opinion row gives
  // the place as the opinion names it and the suspended months when stated.
  { key: 'sentence', label: 'Sentence' },
  { key: 'imposed_on', label: 'Imposed' },
  { key: 'age_at_sentencing', label: 'Age' },
  { key: 'after_appeal', label: 'On appeal' },
  { key: 'known_from', label: 'Known from' },
];

/** DOC's commitment and suspension in one phrase: "MSP/MWP · some suspended". */
export function docSentence(rec) {
  const parts = [rec.commit_type, rec.suspension && `${String(rec.suspension).toLowerCase()} suspended`].filter(Boolean);
  if (rec.suspended_months != null && knownFromOf(rec) === 'opinion') parts.push(`${rec.suspended_months} mo suspended`);
  return parts.join(' · ');
}

function formatTerm(term) {
  if (term == null) return '—';
  if (typeof term === 'object') {
    const value = term.value ?? term.length ?? null;
    const unit = term.unit ?? '';
    if (value == null) return '—';
    return `${value} ${unit}`.trim();
  }
  return String(term);
}

function fateText(rec) {
  if (rec.after_appeal) return afterAppealLabel(rec.after_appeal);
  const set = rec.after_appeal_set || [];
  return set.length ? set.map(afterAppealLabel).join(', ') : '—';
}

function cellValue(rec, key) {
  if (key === 'term') return formatTerm(rec.term);
  if (key === 'sentence') return docSentence(rec) || '—';
  if (key === 'after_appeal') return fateText(rec);
  if (key === 'known_from') return knownFromLabel(knownFromOf(rec));
  if (key === 'description' && rec.description_withheld) return rec.is_total ? 'Total' : '—';
  const v = rec[key];
  return v == null || v === '' ? '—' : String(v);
}

function FilterLine({ filters }) {
  const chips = describeFilters(filters);
  return (
    <div className="sentencing-filterline">
      <span className="sentencing-facet-label">Filter</span>
      {chips.length === 0 && <span className="sentencing-facet-chip">All sentences</span>}
      {chips.map((c) => <span key={c} className="sentencing-facet-chip">{c}</span>)}
      <a href={browseLink(filters)}>Open in Browse</a>
    </div>
  );
}

export function SentencingRecordsCard({ result }) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(1);

  const records = Array.isArray(result?.records) ? result.records : [];

  const sorted = useMemo(() => {
    if (!sortKey) return records;
    return [...records].sort((a, b) => {
      const av = cellValue(a, sortKey);
      const bv = cellValue(b, sortKey);
      return av.localeCompare(bv, undefined, { numeric: true }) * sortDir;
    });
  }, [records, sortKey, sortDir]);

  if (records.length === 0) return null;

  const onSort = (key) => {
    if (sortKey === key) setSortDir((d) => -d);
    else { setSortKey(key); setSortDir(1); }
  };

  const mix = result.by_source || {};
  const mixText = [
    mix.doc != null && `${Number(mix.doc).toLocaleString()} from DOC`,
    mix.opinion != null && `${Number(mix.opinion).toLocaleString()} from an opinion only`,
  ].filter(Boolean).join(' · ');
  const leftOut = leftOutLines(result.left_out);

  return (
    <div className="sentencing-card">
      <div className="sentencing-card-header">
        <span className="sentencing-card-title">Sentences</span>
        <span className="sentencing-card-count">
          {result.returned ?? records.length} shown
          {result.total_matching != null && ` of ${result.total_matching.toLocaleString()} matching`}
          {mixText && ` · ${mixText}`}
        </span>
      </div>
      {leftOut.map((line) => <div key={line} className="sb-meta-leftout">{line}</div>)}
      <div className="sentencing-table-wrap">
        <table className="sentencing-table">
          <thead>
            <tr>
              {RECORD_COLUMNS.map((c) => (
                <th key={c.key} onClick={() => onSort(c.key)} title="Click to sort">
                  {c.label}{sortKey === c.key ? (sortDir === 1 ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((rec, i) => (
              <RecordRow key={`${rec.record_id || rec.docket || 'row'}-${i}`} rec={rec} />
            ))}
          </tbody>
        </table>
      </div>
      {result.filters && <FilterLine filters={result.filters} />}
    </div>
  );
}

function RecordRow({ rec }) {
  const opinions = rec.opinions && rec.opinions.length ? rec.opinions : (rec.opinion ? [rec.opinion] : []);
  return (
    <>
      <tr>
        {RECORD_COLUMNS.map((c) => (
          <td key={c.key}>
            {c.key === 'known_from'
              ? (
                <span className="sb-known-cell">
                  <KnownFromBadge rec={rec} />
                  {opinions.map((o) => <OpinionLinks key={o.opinion_id} opinion={{ ...o, cite: shortCite(o) }} compact />)}
                </span>
              )
              : cellValue(rec, c.key)}
          </td>
        ))}
      </tr>
      {Array.isArray(rec.facts_snippets) && rec.facts_snippets.length > 0 && (
        <tr className="sb-facts-row"><td colSpan={RECORD_COLUMNS.length}><FactSnippets snippets={rec.facts_snippets} /></td></tr>
      )}
    </>
  );
}

/** One headline figure: the value large, its label under it, a quiet note when there is more to say. */
function Tile({ label, value, note, title }) {
  return (
    <div className="sb-stat-tile" title={title}>
      <div className="sb-stat-tile-value">{value}</div>
      <div className="sb-stat-tile-label">{label}</div>
      {note && <div className="sb-stat-tile-note">{note}</div>}
    </div>
  );
}

/** One breakdown: a label at the left, chips with counts to the right. */
function ChipRow({ label, title, children }) {
  return (
    <div className="sb-stat-row" title={title}>
      <span className="sb-stat-key">{label}</span>
      <span className="sb-stat-chips">{children}</span>
    </div>
  );
}

function Chip({ className = 'sb-stat-chip-plain', label, count }) {
  return (
    <span className={`sb-stat-chip ${className}`}>
      <span className="sb-stat-chip-label">{label}</span>
      <span className="sb-stat-chip-n">{Number(count ?? 0).toLocaleString()}</span>
    </span>
  );
}

/**
 * The figures, laid out in plain words (owner, 2026-10-11: no statistical terms for lawyers):
 * tiles for the sentences, the sentencings, the typical term (half shorter, half longer), the
 * span half of the terms fall in, the span nine in ten fall in, and the typical total term;
 * then one row of chips per breakdown (commitment, suspension, where the sentences are known
 * from, what the appeal did). Terms are in months, as the data holds them; the years sit
 * under the figure.
 */
function StatBlock({ stat, showFate = false, mix = null }) {
  if (!stat) return null;
  if (stat.not_applicable) {
    return <div className="so-muted">Not applicable: {stat.not_applicable}</div>;
  }
  const q = stat.term_months || {};
  const t = stat.total_term_months || {};
  const n = (v) => Number(v ?? 0).toLocaleString();
  const span = (s) => (s && s.low != null && s.high != null ? s : null);
  const tiles = [
    { label: 'Sentences', value: n(stat.rows), title: 'Each count sentenced is one sentence; a sentencing with three counts is three sentences' },
    { label: 'Sentencings', value: n(stat.orders), title: 'Sentencing orders: one judgment, however many counts it carries' },
  ];
  if (q.stated > 0) {
    if (q.too_few || q.typical == null) {
      // Under 20 terms only the shortest and the longest are said: too few to call one typical.
      const few = `Only ${n(q.stated)} state a term: too few to call one typical`;
      if (q.shortest === q.longest) tiles.push({ label: 'The term', value: monthsShort(q.shortest), note: yearsNote(q.shortest) || undefined, title: few });
      else {
        tiles.push({ label: 'Shortest term', value: monthsShort(q.shortest), note: yearsNote(q.shortest) || undefined, title: few });
        tiles.push({ label: 'Longest term', value: monthsShort(q.longest), note: yearsNote(q.longest) || undefined, title: few });
      }
    } else {
      const half = span(q.half_between);
      const nine = span(q.nine_in_ten_between);
      tiles.push({
        label: 'Typical term', value: monthsShort(q.typical),
        note: [yearsNote(q.typical), 'half shorter, half longer', `${n(q.stated)} state a term`].filter(Boolean).join(' · '),
        title: 'Per offense: half the terms are shorter than this, half longer',
      });
      if (half) {
        tiles.push({
          label: 'Half fall between', value: monthsRange(half.low, half.high), note: yearsNote(half.low, half.high) || undefined,
          title: `Half of the terms fall in this span: one in four is shorter than ${monthsShort(half.low)}, one in four longer than ${monthsShort(half.high)}`,
        });
      }
      if (nine) {
        tiles.push({
          label: '9 in 10 fall between', value: monthsRange(nine.low, nine.high), note: yearsNote(nine.low, nine.high) || undefined,
          title: 'Nine in ten terms fall in this span. The shortest 1 in 20 and the longest 1 in 20 are left out, so one odd entry (DOC records some sentences as 999 years) cannot set it',
        });
      }
    }
  }
  if (t.stated > 0 && !t.too_few && t.typical != null) {
    const thalf = span(t.half_between);
    tiles.push({
      label: 'Typical total term', value: monthsShort(t.typical),
      note: [yearsNote(t.typical), 'all counts of a sentencing together', `${n(t.stated)} state a total`].filter(Boolean).join(' · '),
      title: `The total term of a sentencing, all its counts together: half are shorter than ${monthsShort(t.typical)}, half longer${thalf ? `; half fall between ${monthsRange(thalf.low, thalf.high)}` : ''}`,
    });
  }
  const known = KNOWN_FROM.filter((k) => mix?.[k.key]?.rows);
  return (
    <>
      <div className="sb-stat-tiles">{tiles.map((x) => <Tile key={x.label} {...x} />)}</div>
      {(stat.commit_types || []).length > 0 && (
        <ChipRow label="Commitment" title="Where the sentence is served, as DOC words it, counted per sentencing">
          {stat.commit_types.map((c) => <Chip key={c.value} label={c.value} count={c.count} />)}
        </ChipRow>
      )}
      {(stat.suspensions || []).some((x) => x.value !== 'Not stated') && (
        <ChipRow label="Suspended" title="DOC records how much was suspended only as none / some / all, never in months; counted per sentencing">
          {stat.suspensions.map((x) => <Chip key={x.value} label={x.value} count={x.count} />)}
        </ChipRow>
      )}
      {known.length > 0 && (
        <ChipRow label="Known from" title={KNOWN_FROM_NOTE}>
          {known.map((k) => <Chip key={k.key} className={`sb-known sb-known-${k.key}`} label={k.label} count={mix[k.key].rows} />)}
        </ChipRow>
      )}
      {showFate && (stat.after_appeal || []).length > 0 && (
        <ChipRow label="On appeal" title="What the appeal did, per count, over the sentences known from an opinion only">
          {stat.after_appeal.map((f) => <Chip key={f.value} className={`so-fate so-fate-${f.value}`} label={afterAppealLabel(f.value)} count={f.count} />)}
        </ChipRow>
      )}
    </>
  );
}

export const LENSES = [
  { key: 'all', label: 'All sentences', title: 'Every sentence the filters match' },
  { key: 'doc', label: 'DOC only', title: 'Only the sentences DOC holds (known from DOC, or from DOC and an opinion)' },
  { key: 'appealed', label: 'Appealed cases only', title: 'Only the sentences with an opinion on the case (known from DOC and an opinion, or from an opinion only)' },
];

/**
 * One statistic over the pool, the mix under it, and the two lenses one click away.
 *
 * `applied` (the Browse screen): the lens is a filter — the caller turns it into "Known from"
 * chips (appealed cases = every sentence with an opinion), so the list, the count and these
 * figures move together, and the figures shown are always the result's own (`summary.all`).
 * Without it (a chat card, which has no list), the lens only picks which of the three blocks
 * to show; there "Appealed cases only" is the server's block of sentences known from an
 * opinion only.
 */
export function LensedStatistic({ summary, lens, onLens, compact = false, applied = false }) {
  if (!summary?.all) return null;
  const stat = applied || lens === 'all' ? summary.all : lens === 'doc' ? summary.doc_only : summary.appealed_only;
  const fate = lens === 'appealed' ? summary.appealed_only?.after_appeal : null;
  return (
    <div className={`sb-stat sb-stat-one${compact ? ' sb-stat-compact' : ''}`}>
      <div className="sb-stat-head">
        <div className="sb-stat-lenses" role="tablist" aria-label="Which sentences the statistic covers">
          {LENSES.map((l) => (
            <button key={l.key} role="tab" aria-selected={lens === l.key} title={l.title} className={`sb-lens${lens === l.key ? ' sb-lens-active' : ''}`} onClick={() => onLens(l.key)}>
              {l.label}
            </button>
          ))}
        </div>
        {applied && <span className="sb-stat-caption">over everything the filters match</span>}
      </div>
      <StatBlock stat={fate ? { ...stat, after_appeal: fate } : stat} showFate={lens === 'appealed'} mix={summary.mix} />
    </div>
  );
}

export function SentencingSummaryCard({ result }) {
  const [lens, setLens] = useState('all');
  if (!result) return null;
  if (!result.all && (result.doc || result.appellate)) {
    // an answer from before the pool: the two statistics it was built from
    return (
      <div className="sentencing-card">
        <div className="sentencing-card-header">
          <span className="sentencing-card-title">Sentencing figures</span>
        </div>
        <div className="sb-stats">
          <div className="sb-stat"><div className="sb-stat-title">DOC records</div><StatBlock stat={result.doc} /></div>
          <div className="sb-stat"><div className="sb-stat-title">Known from an opinion only</div><StatBlock stat={result.appellate} showFate /></div>
        </div>
        {result.filters && <FilterLine filters={result.filters} />}
      </div>
    );
  }
  if (!result.all) return null;
  // "Open in Browse" opens the set the figures on show were computed over: the lens is part
  // of it (the card's "DOC only" = the sentences DOC holds; "Appealed cases only" = the
  // server's block of sentences known from an opinion only) unless the filter already says.
  const linkFilters = result.filters && lens !== 'all' && !result.filters.known_from
    ? { ...result.filters, known_from: lens === 'doc' ? ['doc', 'both'] : ['opinion'] }
    : result.filters;
  return (
    <div className="sentencing-card">
      <div className="sentencing-card-header">
        <span className="sentencing-card-title">Sentencing figures</span>
        <span className="sentencing-card-count">each sentence counted once</span>
      </div>
      <LensedStatistic summary={result} lens={lens} onLens={setLens} compact />
      {linkFilters && <FilterLine filters={linkFilters} />}
    </div>
  );
}

export function SentencingAuthorityCard({ result }) {
  const passages = Array.isArray(result?.passages) ? result.passages : [];
  const holdings = Array.isArray(result?.holdings) ? result.holdings : [];
  if (passages.length === 0 && holdings.length === 0) return null;
  return (
    <div className="sentencing-card">
      <div className="sentencing-card-header">
        <span className="sentencing-card-title">Montana Supreme Court authority</span>
        <span className="sentencing-card-count">
          {passages.length} passage{passages.length === 1 ? '' : 's'}
          {holdings.length > 0 && `, ${holdings.length} holding${holdings.length === 1 ? '' : 's'}`}
        </span>
      </div>
      {passages.map((p, i) => (
        <div key={`p-${i}`} className="sentencing-authority">
          <div className="sentencing-authority-head">
            <OpinionLinks opinion={{ ...p.opinion, cite: `${p.opinion?.case_name ? `${p.opinion.case_name}, ` : ''}${p.opinion?.cite}, ${p.pinpoint}` }} />
            {!p.citable && <span className="so-tag">Noncitable</span>}
          </div>
          <blockquote className="so-para">{p.text}</blockquote>
        </div>
      ))}
      {holdings.length > 0 && <div className="sentencing-facet-label">Holdings, as summarised from the opinion</div>}
      {holdings.map((h) => (
        <div key={h.issue_id} className="sentencing-authority">
          <div className="sentencing-authority-head">
            <OpinionLinks opinion={{ ...h.opinion, cite: `${h.opinion?.cite}, ${h.pinpoint}` }} />
            {!h.citable && <span className="so-tag">Noncitable</span>}
          </div>
          <div><strong>{h.issue_label}</strong> — {h.holding}</div>
        </div>
      ))}
    </div>
  );
}
